/*
 *   File : referral.js
 *   Author : https://evoqins.com
 *   Description : Referral tab for customer detail
 *   Integrations : 'react-toastify'
 *   Version : 1.0.0
*/

import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { CustomDataTable } from '../../../Components/DataTable';
import { CustomTooltip, EmptyScreen, Loader } from '../../../Components/Others';
import { TextButton } from '../../../Components/Buttons';
import { APIService } from '../../../Service';

import color from '../../../Style/color.scss';
import style from '../../../Style/Component/customer.module.scss';

const SUMMARY_CARDS = [
    // {
    //     key: 'total_referrals',
    //     label: 'Total Referrals',
    //     tooltipId: 'tooltip-total-referrals',
    //     tooltip: 'Total users referred by this user.'
    // },
    {
        key: 'invited',
        label: 'Invited',
        tooltipId: 'tooltip-invited-referrals',
        tooltip: 'Referred users who have not completed sign-up.'
    },
    {
        key: 'in_progress',
        label: 'In Progress',
        tooltipId: 'tooltip-in-progress-referrals',
        tooltip: 'Referred users who started sign-up but have not completed it.'
    },
    {
        key: 'invested',
        label: 'Invested',
        tooltipId: 'tooltip-invested-referrals',
        tooltip: 'Referred users who completed sign-up and made an investment.'
    }
];

const _getReferralStatus = (row) => {
    const signup = (row.signup_progress || '').toLowerCase();

    if (!signup) {
        return null;
    }

    if (signup.includes('invested')) {
        return {
            label: row.signup_progress,
            className: style.e_referral_invested
        };
    }

    if (signup.includes('kyc')) {
        return {
            label: row.signup_progress,
            className: style.e_referral_kyc_done
        };
    }

    return {
        label: row.signup_progress,
        className: style.e_referral_signed_up
    };
};

const Referral = () => {
    const { id } = useParams();
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const isRendered = useRef(true);

    const [loader, setLoader] = useState(true);
    const [tableLoader, setTableLoader] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [referralData, setReferralData] = useState({});

    useEffect(() => {
        if (isRendered.current === true) {
            _getReferrals();
        }
        isRendered.current = false;
    }, []);

    const _handleViewProfile = (customer_id) => {
        if (!customer_id) {
            return;
        }

        navigate(pathname.includes("support") ? `/support/${customer_id}` : `/customers/${customer_id}`, {
            state: {
                source: pathname.includes("support") ? "customer_support" : "normal_listing",
                inner_tab_index: 1
            }
        });
    };

    const _handleTablePageChange = (page_num) => {
        setTableLoader(true);
        setCurrentPage(page_num);
        _getReferrals(page_num);
    };

    const REFERRAL_TABLE_COLUMN = [
        {
            name: 'User ID',
            selector: row => row.user_id || '-',
            width: '110px'
        },
        {
            name: 'Referred User',
            selector: row => row.name || '-',
            cell: (row) =>
                <>
                    {row.name || "-"}
                </>,
            width: '220px',
            sortable: true
        },
        {
            name: 'Invited On',
            selector: row => row.invited_on || '-',
            width: '120px'
        },
        {
            name: 'Signed-Up Status',
            cell: (row) => {
                const status = _getReferralStatus(row);

                if (!status) {
                    return '-';
                }

                return (
                    <span className={`${style.e_referral_status} ${status.className} e-primary-font e-font-12 e-font-weight-400 line-height-16px e-text-white`}>
                        {status.label}
                    </span>
                );
            },
            width: '180px'
        },
        {
            name: 'Action',
            cell: (row) =>
                <TextButton name="View profile"
                    onClick={() => _handleViewProfile(row.customer_id)} />,
            width: '140px'
        }
    ];

    const _getReferrals = (page_num = currentPage) => {
        const is_post = true;
        const url = 'user/referral-list';
        const request = {
            customer_id: Number(id),
            page_num: page_num,
            page_size: 10
        };

        APIService(is_post, url, request).then((response) => {
            if (response.status_code === 200) {
                setReferralData(response.data || {});
            } else {
                setReferralData({ referrals: [], summary: {} });
            }
            setLoader(false);
            setTableLoader(false);
        });
    };

    const referralList = referralData.referrals || [];
    const summaryData = referralData.summary || {};

    return (
        <Fragment>
            {loader ?
                <Loader className="min-vh-50" />
                :
                <>
                    <div className='padding-32px-top padding-32px-lr'>
                        <h6 className='mb-0 e-text-smoky-black e-primary-font e-font-16 e-font-weight-600 line-height-20px'>
                            Referral details
                        </h6>

                        <div className='row gx-3 pt-3'>
                            {SUMMARY_CARDS.map((card) => (
                                <div className='col-2 '
                                    key={card.key}>
                                    <div className='e-bg-lavender-dark border-radius-4 p-3 d-flex flex-column h-100 gap-2 justify-content-between'>
                                        <div className='d-flex align-items-center gap-1 justify-content-between'>
                                            <p className='mb-0 e-text-dark-silver e-primary-font e-font-14 e-font-weight-400 line-height-16px'>
                                                {card.label}
                                            </p>
                                            <CustomTooltip size={14}
                                                color={color.dark_silver}
                                                id={card.tooltipId}
                                                content={card.tooltip} />
                                        </div>
                                        <p className='mb-0 e-text-chinese-black e-primary-font e-font-14 e-font-weight-600 line-height-20px'>
                                            {summaryData[card.key] ?? 0}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className='pt-3 mt-1'>
                        {tableLoader ?
                            <Loader className="min-vh-50" />
                            :
                            referralList.length !== 0 ? (
                                <div className='e-data-table-wrapper-default-updated pt-3 pb-4'>
                                    <CustomDataTable columns={REFERRAL_TABLE_COLUMN}
                                        data={referralList}
                                        pagination={true}
                                        paginationPerPage={10}
                                        paginationTotalRows={referralData.total_count || referralList.length}
                                        paginationDefaultPage={referralData.current_page || currentPage}
                                        lastPage={referralData.total_page}
                                        onChangePage={_handleTablePageChange} />
                                </div>
                            ) : (
                                <EmptyScreen className="min-vh-50 py-5 my-5"
                                    image={require('../../../Assets/Image/NotFound/no-data.png')}
                                    title='No referrals found'
                                    description="This customer has not referred anyone yet." />
                            )
                        }
                    </div>
                </>
            }
        </Fragment>
    );
};

export default Referral;
